import React, { useEffect, useState } from 'react';
import Button from '../Button/Button';
import './DeleteMemberModal.css';

export interface DeleteMemberInfo {
  id: number;
  nom: string;
  prenom: string;
  email: string;
}

export interface DeleteMemberAvailable {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  role?: string;
}

interface Dependencies {
  bdc_count?: number;
  ms_count?: number;
  lot_contrat_count?: number;
}

interface Props {
  member: DeleteMemberInfo;
  availableMembers: DeleteMemberAvailable[];
  dependenciesEndpoint: string;
  transferEndpoint: string;
  deleteEndpoint: string;
  onSuccess: (memberId: number) => void;
  onClose: () => void;
}

type Step = 'loading' | 'confirm' | 'transfer' | 'submitting';

const CrossIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DeleteMemberModal: React.FC<Props> = ({
  member,
  availableMembers,
  dependenciesEndpoint,
  transferEndpoint,
  deleteEndpoint,
  onSuccess,
  onClose,
}) => {
  const [step, setStep] = useState<Step>('loading');
  const [dependencies, setDependencies] = useState<Dependencies | null>(null);
  const [targetId, setTargetId] = useState<string>('');
  const [error, setError] = useState<string>('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchDependencies = async () => {
      try {
        const res = await fetch(dependenciesEndpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });
        const json = await res.json();
        if (!res.ok) {
          setError(json.message || 'Erreur lors du chargement des dépendances.');
          setStep('confirm');
          return;
        }
        const deps: Dependencies = json.data ?? json;
        setDependencies(deps);

        const hasDeps =
          (deps.bdc_count ?? 0) > 0 ||
          (deps.ms_count ?? 0) > 0 ||
          (deps.lot_contrat_count ?? 0) > 0;

        setStep(hasDeps ? 'transfer' : 'confirm');
      } catch {
        setError('Impossible de vérifier les dépendances. Veuillez réessayer.');
        setStep('confirm');
      }
    };

    fetchDependencies();
  }, [dependenciesEndpoint, token]);

  const handleDelete = async () => {
    setError('');
    setStep('submitting');

    try {
      const res = await fetch(deleteEndpoint, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || 'Erreur lors de la suppression.');
        setStep('confirm');
        return;
      }
      onSuccess(member.id);
    } catch {
      setError('Erreur réseau. Veuillez réessayer.');
      setStep('confirm');
    }
  };

  const handleTransferAndDelete = async () => {
    if (!targetId) {
      setError('Veuillez sélectionner un repreneur.');
      return;
    }

    setError('');
    setStep('submitting');

    try {
      const res = await fetch(transferEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ target_role_entry_id: parseInt(targetId, 10) }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || 'Erreur lors du transfert.');
        setStep('transfer');
        return;
      }
      onSuccess(member.id);
      // Reattribution des titularites annuaire : rechargement complet pour refleter l'etat
      // a jour (les titularites transferees sortent du perimetre gere en etat local).
      window.location.reload();
    } catch {
      setError('Erreur réseau. Veuillez réessayer.');
      setStep('transfer');
    }
  };

  const hasDeps =
    dependencies &&
    ((dependencies.bdc_count ?? 0) > 0 ||
      (dependencies.ms_count ?? 0) > 0 ||
      (dependencies.lot_contrat_count ?? 0) > 0);

  const formatPrenom = (p: string) => p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : '';
  const formatNom = (n: string) => n ? n.toUpperCase() : '';
  const memberFullName = `${formatPrenom(member.prenom)} ${formatNom(member.nom)}`;
  const isSubmitting = step === 'submitting';

  return (
    <div
      className="delete-member-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-member-modal-title"
    >
      <div className="delete-member-modal" onClick={(e) => e.stopPropagation()}>
        <div className="delete-member-modal__header">
          <h2 id="delete-member-modal-title">Supprimer le compte</h2>
          <button className="delete-member-modal__close" onClick={onClose} aria-label="Fermer">
            <CrossIcon />
          </button>
        </div>

        <div className="delete-member-modal__body">
          {step === 'loading' && (
            <p className="delete-member-modal__confirm-text">Vérification des dépendances...</p>
          )}

          {step === 'transfer' && hasDeps && (
            <>
              <div className="delete-member-modal__deps">
                <p>
                  <strong>{memberFullName}</strong> a des données actives qui doivent être transférées :
                </p>
                <ul>
                  {(dependencies!.bdc_count ?? 0) > 0 && (
                    <li>{dependencies!.bdc_count} bon(s) de commande actif(s)</li>
                  )}
                  {(dependencies!.ms_count ?? 0) > 0 && (
                    <li>{dependencies!.ms_count} marché(s) subséquent(s) actif(s)</li>
                  )}
                  {(dependencies!.lot_contrat_count ?? 0) > 0 && (
                    <li>{dependencies!.lot_contrat_count} lot(s) de contrat assigné(s)</li>
                  )}
                </ul>
              </div>

              <div className="delete-member-modal__select-group">
                <label htmlFor="delete-member-target">Transférer vers :</label>
                <select
                  id="delete-member-target"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">-- Sélectionner un repreneur --</option>
                  {availableMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.prenom ? m.prenom.charAt(0).toUpperCase() + m.prenom.slice(1).toLowerCase() : ''} {m.nom ? m.nom.toUpperCase() : ''} ({m.email}){m.role ? ` — ${m.role}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {(step === 'confirm' || (step === 'transfer' && !hasDeps)) && (
            <p className="delete-member-modal__confirm-text">
              Confirmer la suppression du compte de <strong>{memberFullName}</strong> ({member.email}) ?
              Cette action est irréversible.
            </p>
          )}

          {error && <p className="delete-member-modal__error">{error}</p>}
        </div>

        <div className="delete-member-modal__actions">
          <Button
            value="Annuler"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          />

          {step === 'transfer' && hasDeps ? (
            <Button
              value={isSubmitting ? 'En cours...' : 'Transférer et supprimer'}
              variant="primary"
              onClick={handleTransferAndDelete}
              disabled={isSubmitting || !targetId}
            />
          ) : (
            <Button
              value={isSubmitting ? 'En cours...' : 'Supprimer'}
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting || step === 'loading'}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteMemberModal;
